// src/components/clients/TripMap.js
import React, { useEffect, useRef } from 'react';

/**
 * Shows three things on one map:
 *  - Planned route: origin (A) → destination (B), dashed blue line (from OSRM)
 *  - Actual trail: solid line connecting recorded history points, in the truck's color
 *  - Live marker: the truck's current/last-known position
 */
export default function TripMap({ origin, destination, plannedRoute, trail, currentPosition, truckColor = '#3b82f6', height = 320 }) {
  const mapRef = useRef(null);
  const leafRef = useRef(null);
  const layersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current) return;

    function clearLayers() {
      if (!leafRef.current) return;
      layersRef.current.forEach(l => leafRef.current.removeLayer(l));
      layersRef.current = [];
    }

    function draw(L) {
      if (!leafRef.current) {
        leafRef.current = L.map(mapRef.current, {
          center: [14.5995, 120.9842], zoom: 12, zoomControl: true, attributionControl: false,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(leafRef.current);
      }
      const map = leafRef.current;
      clearLayers();

      const bounds = [];
      const pinIcon = (color, label) => L.divIcon({
        html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;background:${color};transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center">
                 <span style="transform:rotate(45deg);font-size:11px;color:#fff;font-weight:800;">${label}</span>
               </div>`,
        className: '', iconSize: [26, 26], iconAnchor: [13, 26],
      });

      // Origin / destination pins
      if (origin?.lat) {
        const m = L.marker([origin.lat, origin.lng], { icon: pinIcon('#10b981', 'A') }).addTo(map).bindPopup('Origin');
        layersRef.current.push(m);
        bounds.push([origin.lat, origin.lng]);
      }
      if (destination?.lat) {
        const m = L.marker([destination.lat, destination.lng], { icon: pinIcon('#f97316', 'B') }).addTo(map).bindPopup('Destination');
        layersRef.current.push(m);
        bounds.push([destination.lat, destination.lng]);
      }

      // Planned route (dashed)
      if (plannedRoute && plannedRoute.length > 1) {
        const line = L.polyline(plannedRoute, { color: '#3b82f6', weight: 3, opacity: 0.55, dashArray: '7,7' }).addTo(map);
        layersRef.current.push(line);
      }

      // Actual traveled trail (solid, truck color)
      if (trail && trail.length > 1) {
        const line = L.polyline(trail, { color: truckColor, weight: 4, opacity: 0.9 }).addTo(map);
        layersRef.current.push(line);
        trail.forEach(pt => bounds.push(pt));

        // Small dots along the trail marking recorded stops
        trail.forEach((pt, i) => {
          if (i === 0 || i === trail.length - 1) return; // skip endpoints, handled separately
          const dot = L.circleMarker(pt, { radius: 3, color: truckColor, fillColor: truckColor, fillOpacity: 1, weight: 1 }).addTo(map);
          layersRef.current.push(dot);
        });
      }

      // Live / last-known position marker
      if (currentPosition?.lat) {
        const truckIcon = L.divIcon({
          html: `<div style="position:relative;width:34px;height:34px;">
                   <div style="position:absolute;inset:0;border-radius:50%;background:${truckColor};opacity:0.25;animation:tripPulse 2s infinite;"></div>
                   <div style="position:absolute;inset:5px;border-radius:50%;background:${truckColor};border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;font-size:13px;">🚛</div>
                 </div>`,
          className: '', iconSize: [34, 34], iconAnchor: [17, 17],
        });
        const m = L.marker([currentPosition.lat, currentPosition.lng], { icon: truckIcon, zIndexOffset: 1000 }).addTo(map).bindPopup('Current location');
        layersRef.current.push(m);
        bounds.push([currentPosition.lat, currentPosition.lng]);
      }

      if (bounds.length >= 2) {
        map.fitBounds(bounds, { padding: [32, 32] });
      } else if (bounds.length === 1) {
        map.setView(bounds[0], 13);
      }
    }

    const L = window.L;
    if (!L) {
      const existing = document.querySelector('script[src*="leaflet.js"]');
      if (!existing) {
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        s.crossOrigin = 'anonymous';
        s.onload = () => draw(window.L);
        document.head.appendChild(s);
      } else {
        existing.addEventListener('load', () => draw(window.L));
      }
    } else {
      draw(L);
    }
  }, [origin, destination, plannedRoute, trail, currentPosition, truckColor]);

  useEffect(() => () => {
    if (leafRef.current) { leafRef.current.remove(); leafRef.current = null; }
  }, []);

  return (
    <div style={{ height, borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border)', position: 'relative' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      <style>{`@keyframes tripPulse { 0% { transform: scale(1); opacity: 0.35; } 100% { transform: scale(1.9); opacity: 0; } }`}</style>
    </div>
  );
}
